/*
 * Roughly follows the CASL ability based authorization model (https://casl.js.org/v4/en)
 * NOTE: this list should be eventually be fetched from the backend and harmonized with
 * the authorization layer of the API service.
 *
 * Subscription types are derived from the user's selected_program.subscriptionType:
 * - Users with a program have access based on the program's subscriptionType
 * - Users without a program have limited "free" access
 */

// Subscription type checkers - derive from selectedProgram.subscriptionType
const hasPaidSubscription = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  // Any program is considered "paid" - Free users have no program
  return !!selectedProgram;
};

const hasNoProgram = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  return !selectedProgram;
};

const hasLitePlusSubscription = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  return selectedProgram && selectedProgram.subscriptionType === 'Lite+';
};

const hasRhetorichEventsSubscription = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  return selectedProgram && selectedProgram.subscriptionType === 'RhetorichEvents';
};

const hasLiteSubscription = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  return selectedProgram && selectedProgram.subscriptionType === 'Lite';
};

const hasPowerPrepSubscription = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  return selectedProgram && selectedProgram.subscriptionType === 'Power Prep';
};

const hasExecutiveSubscription = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  return selectedProgram && selectedProgram.subscriptionType === 'Executive';
};

const hasPaidSubscriptionWithinProgram = ({ store }) => {
  const selectedProgram = store.getters['programs/selectedProgram'];
  // User has a program = paid subscription within program
  return !!selectedProgram;
};

const hasRefineScriptQuota = ({ store }) => store.getters['aiQuota/hasRefineScriptQuota'];
const hasImproveSpeechQuota = ({ store }) => store.getters['aiQuota/hasImproveSpeechQuota'];
const hasRoleplayQuota = ({ store }) => store.getters['aiQuota/hasRoleplayQuota'];

export default {
  data() {
    return {
      permissions: [
        {
          action: 'create',
          subject: 'Course',
          conditions: {},
          roles: ['Administrator'],
        },
        {
          action: 'update',
          subject: 'Course',
          conditions: {},
          roles: ['Administrator'],
        },
        {
          action: 'view',
          subject: 'Course',
          conditions: {
            hasLiteSubscription,
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
        {
          action: 'view',
          subject: 'PartialCourse',
          conditions: {
            hasNoProgram,
          },
          roles: ['Student'],
        },
        {
          action: 'view',
          subject: 'Feedback',
          conditions: {
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Coordinator', 'Student'],
        },
        {
          action: 'manage',
          subject: 'Feedback',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'create',
          subject: 'Program',
          conditions: {},
          roles: ['Administrator'],
        },
        {
          action: 'view',
          subject: 'Program',
          conditions: {
            hasPaidSubscription,
          },
          roles: ['Administrator', 'Coach', 'Coordinator', 'Student'],
        },
        {
          action: 'manage',
          subject: 'Program',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'view',
          subject: 'Certificate',
          conditions: {
            hasRhetorichEventsSubscription,
            hasLiteSubscription,
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'manage',
          subject: 'Certificate',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'manage',
          subject: 'Document',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'view',
          subject: 'Document',
          conditions: {
            hasRhetorichEventsSubscription,
            hasLiteSubscription,
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'create',
          subject: 'Session',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'update',
          subject: 'Session',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'book',
          subject: 'Session',
          conditions: {
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
        {
          action: 'join',
          subject: 'Session',
          conditions: {
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'manage',
          subject: 'Session',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'create',
          subject: 'Video',
          conditions: {},
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'share',
          subject: 'Video',
          conditions: {
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
        {
          action: 'review',
          subject: 'Video',
          conditions: {
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
        {
          action: 'generate',
          subject: 'Script',
          conditions: {
            hasNoProgram,
            hasRhetorichEventsSubscription,
            hasLiteSubscription,
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'refine',
          subject: 'Script',
          conditions: {
            hasRefineScriptQuota,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'improve_speech',
          subject: 'Script',
          conditions: {
            hasImproveSpeechQuota,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'practice',
          subject: 'RoleplaySession',
          conditions: {
            hasRoleplayQuota,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'analyze',
          subject: 'Video',
          conditions: {
            hasPaidSubscriptionWithinProgram,
            hasAnalysisQuota({ store }) {
              const selectedProgram = store.getters['programs/selectedProgram'];

              // Users with a program have unlimited analysis
              if (selectedProgram) {
                return true;
              }

              // Users without a program are limited to 2 videos
              const videoList = store.getters['videos/allVideos'];
              const processingVideos = videoList.filter(v => v.status === 'Analyzing');
              const processedVideos = videoList.filter(v => v.status === 'Analyzed');

              return processedVideos.length + processingVideos.length < 2;
            },
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'view',
          subject: 'PartialVideoResults',
          conditions: {
            hasNoProgram,
          },
          roles: ['Student'],
        },
        {
          action: 'view',
          subject: 'VideoResults',
          conditions: {
            hasPaidSubscriptionWithinProgram,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'delete',
          subject: 'Program',
          conditions: {},
          roles: ['Administrator'],
        },
        {
          action: 'view',
          subject: 'Faq',
          conditions: {
            hasPaidSubscriptionWithinProgram,
          },
          roles: ['Student', 'Coordinator', 'Coach', 'Administrator'],
        },
        {
          action: 'manage',
          subject: 'Faq',
          conditions: {},
          roles: ['Administrator'],
        },
        {
          action: 'view',
          subject: 'CoachFaq',
          conditions: {},
          roles: ['Coach', 'Coordinator', 'Administrator'],
        },
        {
          action: 'view',
          subject: 'Exercises',
          conditions: {
            hasPaidSubscription,
          },
          roles: ['Student', 'Coordinator', 'Coach', 'Administrator'],
        },
        {
          action: 'view',
          subject: 'SharedExercises',
          conditions: {
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Student', 'Coordinator', 'Coach', 'Administrator'],
        },
        {
          action: 'view',
          subject: 'PartialExercises',
          conditions: {
            hasNoProgram,
          },
          roles: ['Student'],
        },
        {
          action: 'manage',
          subject: 'Activity',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'view',
          subject: 'ProgramMembers',
          conditions: {},
          roles: ['Administrator', 'Coach', 'Coordinator'],
        },
        {
          action: 'manage',
          subject: 'ProgramMembers',
          conditions: {},
          roles: ['Administrator', 'Coordinator'],
        },
        {
          action: 'view',
          subject: 'StudentsStats',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'view',
          subject: 'RhetorichEventsDashboard',
          conditions: {
            hasRhetorichEventsSubscription,
          },
          roles: ['Student'],
        },
        {
          action: 'view',
          subject: 'FreeDashboard',
          conditions: {
            hasNoProgram,
          },
          roles: ['Student', 'Administrator', 'Coach'],
        },
        {
          action: 'view',
          subject: 'LiteDashboard',
          conditions: {
            hasLiteSubscription,
          },
          roles: ['Student'],
        },
        {
          action: 'view',
          subject: 'StudentDashboard',
          conditions: {
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
        {
          action: 'view',
          subject: 'CoachDashboard',
          conditions: {},
          roles: ['Coach', 'Administrator'],
        },
        {
          action: 'view',
          subject: 'CoordinatorDashboard',
          conditions: {},
          roles: ['Coordinator'],
        },
        {
          action: 'view',
          subject: 'CoachingSessions',
          conditions: {
            hasNoProgram,
            hasLiteSubscription,
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
        {
          action: 'view',
          subject: 'MyAvailability',
          conditions: {},
          roles: ['Administrator', 'Coach'],
        },
        {
          action: 'view',
          subject: 'SharedWithMe',
          conditions: {
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
        {
          action: 'view',
          subject: 'MySessions',
          conditions: {
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Student', 'Administrator', 'Coach'],
        },
        {
          action: 'view',
          subject: 'MyRecordings',
          conditions: {
            hasRhetorichEventsSubscription,
            hasLiteSubscription,
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coordinator', 'Coach', 'Student'],
        },
        {
          action: 'view',
          subject: 'RolePlayPractice',
          conditions: {
            hasLiteSubscription,
            hasLitePlusSubscription,
            hasPowerPrepSubscription,
            hasExecutiveSubscription,
          },
          roles: ['Administrator', 'Coach', 'Student'],
        },
      ],
    };
  },
  computed: {
    userRole() {
      return this.currentUser ? this.currentUser.role : 'Unknown';
    },
    currentUser() {
      return this.$store.getters['users/currentUser'];
    },
  },
  methods: {
    checkConditions(permission) {
      const conditions = Object.values(permission.conditions);

      if (!conditions.length || this.currentUser.role !== 'Student') {
        return true;
      }

      return conditions.some(condition => condition({
        currentUser: this.currentUser,
        store: this.$store,
      }));
    },
    can(action, subject) {
      const permission = this.permissions.find(p => p.action === action && p.subject === subject);

      if (!permission) {
        return false;
      }

      return permission.roles.includes(this.userRole) && this.checkConditions(permission);
    },
  },
};
